import { createFileRoute, redirect } from '@tanstack/react-router'
import { DEFAULT_AGENT_ID } from '@/stores/agent.store'
import { isRealProject } from '@/api/client'
import { queryClient } from '@/app/providers'
import {
  projectAgentsQueryOptions,
  pickLastActiveAgentId,
} from '@/api/queries/agents'
import { tabsMetaQueryOptions } from '@/api/queries/input-canvas'
import { useCanvasStore } from '@/stores/canvas.store'

/**
 * Bare /$projectId — redirect into the workspace on the agent the user last
 * interacted with (newest conversation activity), falling back to the default
 * agent for a fresh project or if the lookup fails. The project-agents list is
 * usually already warm here: the dashboard prefetches it on hover/click intent
 * (see `prefetchProjectAgents`), and the workspace's own `useProjectAgents`
 * reuses this same cache entry, so the resolve is a cache hit, not an extra
 * round-trip. Drafts ('new') skip the lookup and land on the default agent.
 */
export const Route = createFileRoute('/_app/$projectId/')({
  validateSearch: (search: Record<string, unknown>) => ({
    invite: search.invite === true || search.invite === 'true' ? true : undefined,
  }),
  beforeLoad: async ({ params, search }) => {
    const { projectId } = params
    let agentId: string = DEFAULT_AGENT_ID

    if (isRealProject(projectId)) {
      try {
        const agents = await queryClient.ensureQueryData(
          projectAgentsQueryOptions(projectId),
        )
        agentId = pickLastActiveAgentId(agents) ?? DEFAULT_AGENT_ID
      } catch {
        // Never block opening a project on this lookup — fall back to default.
        agentId = DEFAULT_AGENT_ID
      }
    }

    // Opening from a dashboard/list is a new resume decision, not a continuation
    // of transient canvas state left by the prior workspace visit. Resolve the
    // selected agent's canonical tabs before redirecting so Content Strategy can
    // distinguish output that already exists from output that arrives later in
    // the background. A tabs failure must not block the project from opening;
    // the mounted workspace query retains its normal retry/error behavior.
    const canvasStore = useCanvasStore.getState()
    canvasStore.prepareAgentCanvasResume(agentId)
    if (isRealProject(projectId)) {
      try {
        const tabs = await queryClient.fetchQuery(
          tabsMetaQueryOptions(projectId, agentId),
        )
        canvasStore.captureInitialHasGeneratedOutput(
          agentId,
          tabs.outputTabCount > 0,
        )
      } catch {
        // Navigation remains available when output discovery is temporarily unavailable.
      }
    }

    throw redirect({
      to: '/$projectId/$agentId',
      params: { projectId, agentId },
      search: { invite: search.invite },
    })
  },
})
